import React, { SyntheticEvent, useCallback, useState } from 'react';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions,
  FilterOptionsState,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import { SelectLabelOption } from 'graphql/types';
import { useMessages } from 'modules/intl';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { useCreateCustomLabelOption, useCustomLabelOptions } from '../hooks';
import { EditValueDialog } from './EditValueDialog';

const useStyles = makeStyles((theme) => ({
  addLi: {
    display: 'flex',
    padding: '0px !important',
    color: theme.palette.primary.main
  },
  edit: {
    display: 'flex',
    visibility: 'hidden'
  },
  add: {
    margin: theme.spacing(0),
    padding: theme.spacing(1.5),
    display: 'block',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  li: {
    display: 'flex',
    '&.MuiAutocomplete-option': {
      justifyContent: 'space-between'
    },
    '&:hover $edit': {
      visibility: 'visible'
    }
  }
}));

const useInputStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    '& > div > fieldset': {
      borderColor: ({ disabled }: { disabled: boolean }) =>
        disabled ? 'rgba(0, 0, 0, 0.1) !important' : 'rgba(0, 0, 0, 0.23)'
    },
    '& > label': {
      color: ({ disabled }: { disabled: boolean }) =>
        disabled ? 'rgba(0, 0, 0, 0.24) !important' : 'rgba(0, 0, 0, 0.23)'
    },
    '& > div > input': {
      padding: `${theme.spacing(0.313, 4, 0.313, 0.75)} !important`
    }
  }
}));

type CustomLabelValuePickerProps = {
  label?: CustomLabel;
  value?: SelectLabelOption;
  setValue: (value?: SelectLabelOption) => void;
};

const loadingText = (
  <Typography>
    <FormattedMessage id="customLabels.loading" />
  </Typography>
);

const ADD_VALUE_ID = 'ADD_VALUE_ID';

const popUpWidth = { width: 300 };

const messagesKeys = [
  'customLabels.value',
  'customLabels.typeToAddValue',
  'customLabels.valuePickerPlaceholder'
];

type CustomLabelOption = SelectLabelOption & { inputValue?: string };

const isOptionEqualToValue = (
  option: CustomLabelOption,
  value: CustomLabelOption
) => option.id === value.id;

export const CustomLabelValuePicker: React.FC<CustomLabelValuePickerProps> = ({
  label,
  value,
  setValue
}: CustomLabelValuePickerProps) => {
  const classes = useStyles();
  const inputClasses = useInputStyles({ disabled: !label });
  const messages = useMessages(messagesKeys);
  const [queryText, setQueryText] = useState('');
  const [
    editLabelValue,
    setEditLabelValue
  ] = useState<SelectLabelOption | null>(null);
  const intl = useIntl();

  const {
    loading: creatingCustomLabelOption,
    createCustomLabelOption
  } = useCreateCustomLabelOption();
  const { loading: optionsLoading, options } = useCustomLabelOptions({
    labelId: label?.id,
    queryText
  });

  const onEditClose = useCallback(() => {
    setEditLabelValue(null);
  }, []);

  const handleOnDeleteClose = useCallback(
    (id?: string) => {
      if (id === value?.id) {
        setValue();
      }
      onEditClose();
    },
    [onEditClose, setValue, value?.id]
  );

  const handleOnEditClose = useCallback(
    (updateValue?: SelectLabelOption) => {
      if (updateValue && updateValue.id === value?.id) {
        setValue(updateValue);
      }
      onEditClose();
    },
    [onEditClose, setValue, value?.id]
  );

  const handleEditValue = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, option: CustomLabelOption) => {
      event.preventDefault();
      event.stopPropagation();
      setEditLabelValue(option);
    },
    []
  );

  const handleCreateCustomLabelOption = useCallback(
    async (option: CustomLabelOption) => {
      if (option.inputValue) {
        const newOption = await createCustomLabelOption({
          customLabelId: label!.id,
          name: option.inputValue!
        });
        setValue(newOption);
      }
    },
    [createCustomLabelOption, label, setValue]
  );

  const handleChange = useCallback(
    async (_, option) => {
      if (option && option.id === ADD_VALUE_ID) {
        option.inputValue && (await handleCreateCustomLabelOption(option));
      } else {
        setValue(option);
      }
    },
    [handleCreateCustomLabelOption, setValue]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: CustomLabelOption) => {
      if (option?.id === ADD_VALUE_ID) {
        return (
          <li
            {...props}
            className={classNames(props.className, classes.addLi)}
            data-testid="add-custom-label-option-button"
          >
            <Tooltip title={option.name} disableInteractive>
              <Typography variant="inherit" noWrap className={classes.add}>
                {option.name}
              </Typography>
            </Tooltip>
          </li>
        );
      }

      return (
        <li
          {...props}
          className={classNames(classes.li, props.className)}
          data-testid={`li-${option.id}`}
        >
          <Tooltip followCursor={true} title={option.name} disableInteractive>
            <Typography variant="inherit" noWrap>
              {option.name}
            </Typography>
          </Tooltip>
          <div className={classes.edit}>
            <IconButton
              data-testid={`custom-label-value-edit-button-${option.id}`}
              onClick={(event) => handleEditValue(event, option)}
            >
              <EditIcon />
            </IconButton>
          </div>
        </li>
      );
    },
    [classes.add, classes.addLi, classes.edit, classes.li, handleEditValue]
  );

  const filterOptions = useCallback(
    (
      options: CustomLabelOption[],
      state: FilterOptionsState<CustomLabelOption>
    ) => {
      const defaultFilterOptions = createFilterOptions<CustomLabelOption>();
      const filteredOptions = defaultFilterOptions(options, state);
      const { inputValue: value } = state;
      const inputValue = value?.trim();
      if (
        !filteredOptions.length ||
        (inputValue &&
          !filteredOptions.find(
            (o) => o.name.toLocaleLowerCase() === inputValue.toLocaleLowerCase()
          ))
      ) {
        const name = intl.formatMessage(
          { id: 'customLabels.addValueKey' },
          { option: inputValue }
        );
        filteredOptions.push({
          id: ADD_VALUE_ID,
          name: inputValue ? name : messages['customLabels.typeToAddValue'],
          inputValue: inputValue
        });
      }
      return filteredOptions;
    },
    [intl, messages]
  );

  const getOptionLabel = useCallback((option: CustomLabelOption) => {
    if (option.id === ADD_VALUE_ID) {
      return option.inputValue ?? '';
    }
    return option.name ?? '';
  }, []);

  const onSearch = useCallback(
    (
      _: SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (reason !== 'reset') {
        setQueryText(value);
      }
    },
    []
  );

  const onClose = useCallback(() => {
    setQueryText('');
  }, []);
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={messages['customLabels.value']}
        placeholder={messages['customLabels.valuePickerPlaceholder']}
        size="small"
        classes={inputClasses}
        inputProps={{
          ...params.inputProps,
          'data-testid': 'custom-option-value-input'
        }}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {creatingCustomLabelOption ? (
                <CircularProgress
                  color="inherit"
                  size={20}
                  data-testid="creating-value-loader"
                />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          )
        }}
      />
    ),
    [creatingCustomLabelOption, inputClasses, messages]
  );
  return (
    <>
      <Autocomplete<CustomLabelOption>
        key={`${label?.id}-${value?.name}`}
        data-testid="custom-label-value-search"
        value={value}
        onClose={onClose}
        loadingText={loadingText}
        loading={optionsLoading}
        options={options}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        filterOptions={filterOptions}
        isOptionEqualToValue={isOptionEqualToValue}
        sx={popUpWidth}
        renderInput={renderInput}
        disabled={!label}
        onInputChange={onSearch}
      />
      {editLabelValue && (
        <EditValueDialog
          open
          onClose={onEditClose}
          onEditComplete={handleOnEditClose}
          onDeleteComplete={handleOnDeleteClose}
          value={editLabelValue}
          label={label}
        />
      )}
    </>
  );
};
